import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import OneLineSignupForm from "@/components/auth/OneLineSignupForm";
import {
  loadNatalFormFromStorage,
  isNatalFormComplete,
  seedNatalProfile,
  readNatalFormName,
} from "@/lib/natalProfileSeed";
import { ymGoal } from "@/lib/analytics";
import { pluralBlocks } from "./NatalPreviewBlocks";

import { PRO_GRADIENT } from '../../lib/themePalette';
/**
 * Стена регистрации натального калькулятора.
 *
 * Полный аналог HoraryLimitModal для гостя: там тот же шаг даёт 7%
 * регистраций, натал же отдавал расшифровку и PDF бесплатно и не просил
 * ничего — 359 выданных карт за 24-28.08 против одной регистрации.
 *
 * Само колесо карты остаётся бесплатным и показывается гостю целиком —
 * это крючок. Прячем только три блока расшифровки и скачивание PDF.
 *
 * Гостевая ветка хорарной модалки (пакеты вопросов, PRO, лимиты) сюда
 * намеренно не переносится: у натала нет ни лимита в сутки, ни платных
 * пакетов. Зарегистрированный видит расшифровку целиком, поэтому для него
 * эта модалка не существует вовсе — вызывающий код её просто не открывает.
 *
 * ТРИ ВХОДА, два набора текстов (place):
 *   pdf         — «Скачать PDF карты». Текст утверждён 31.08, не меняется.
 *   full_report — «Открыть весь разбор — N блоков». С 16.09 эта кнопка
 *                 гостю блоки не раскрывает: за 15.09 её нажали 35 человек
 *                 из 120 рекламных визитов, они читали разбор бесплатно и
 *                 не регистрировались. Заголовок называет то же число N,
 *                 что стояло на кнопке, — человек попал туда, куда шёл.
 *
 *   under_wheel — строка «Регистрация в один шаг» в плашке под колесом.
 *                 Тексты те же, что у full_report: речь о том же — открыть
 *                 разбор глубже. Новых формулировок не заводим, различается
 *                 только place у natal_limit.
 *
 *   sticky_mobile — закреплённая кнопка «Открыть полный разбор — N блоков»
 *                 по низу экрана на телефоне (NatalGuestFunnel). Тексты те
 *                 же, что у full_report.
 *   auto_scroll — окно пришло САМО: человек долистал до низа полосы разбора
 *                 и три секунды ничего не нажимал. Один раз за визит.
 *                 Тексты те же, что у full_report.
 *
 * Сама форма, кнопка и цель signup_click у входов общие: различается только
 * верхний текст и параметр place у natal_limit (чтобы Метрика видела, с
 * какого входа пришёл показ). Цели signup_submit и signup_completed у всех
 * входов помечены place=modal_inline: это одна и та же форма в окне.
 *
 * САМА ФОРМА — общий компонент OneLineSignupForm (components/auth): тот же
 * шаг понадобился хорару, и второй такой же формы в проекте быть не должно.
 * Тексты, тост, экран успеха и засев профиля остались ЗДЕСЬ — снаружи это
 * окно не изменилось ни одним символом.
 *
 * РЕГИСТРАЦИЯ ИДЁТ ЗДЕСЬ ЖЕ, одной строкой. Раньше кнопка уводила на
 * /auth/register, и на этом переходе терялось 71%: окно видели 71 человек,
 * до страницы доходили 18, а дошедшие регистрировались все. Значит теряла
 * не форма, а сам переход — поэтому форма переехала в окно, а пароль снят
 * совсем: аккаунт создаётся по одной почте, вход потом по ссылке из письма.
 *
 * После успеха человек ОСТАЁТСЯ НА МЕСТЕ: он уже вошёл, страница
 * перерисовывается сама (полоса превью сменится полными текстами), а что
 * именно доделать — раскрыть разбор или отдать PDF — решает вызывающий код
 * по onRegistered(place). Шага «дата рождения» нет: профиль засевается из
 * гостевой формы тем же only_if_absent, что и на странице регистрации.
 */
// Входы, которым показываем тексты РАЗБОРА, а не PDF. Новых формулировок
// здесь не заводится: набор решает только, какой из двух готовых текстов
// увидит человек. Кнопка на всех этих входах обещает разбор, и текст про
// «скачать PDF» на них читался бы как подмена.
const FULL_REPORT_PLACES = new Set([
  "full_report",    // кнопка «Открыть весь разбор» в полосе превью
  "under_wheel",    // строка «Регистрация в один шаг» в плашке под колесом
  "sticky_mobile",  // закреплённая кнопка по низу экрана на телефоне
  "auto_scroll",    // окно пришло само: долистал полосу и завис
  "day_card",       // «Зарегистрируйся» в плашке «Твой день сегодня»
  // Новые блоки натальной карты под полосой (components/natal/insights):
  // те же тексты разбора, различается только place у natal_limit.
  "insights_elements",   // «Твои стихии»
  "insights_planets",    // «Твоя сила и слабые места»
  "insights_scenarios",  // кнопка под «Основными показателями» и «Талантами»
]);

export default function NatalLimitModal({
  open, onClose, place = "pdf", blocksTotal = 0, onRegistered,
}) {
  // Тексты разбора — и у кнопки «Открыть весь разбор», и у строки в плашке
  // под колесом: человек в обоих случаях пришёл за расшифровкой, а не за PDF.
  const fromFullReport = FULL_REPORT_PLACES.has(place);

  // Адрес, на который ушло письмо. Пока он пуст — в окне стоит форма
  // регистрации одной строкой (OneLineSignupForm); появился — экран успеха.
  // Показываем его обязательно: адрес — единственный вход в аккаунт, и
  // опечатку человек заметит только сейчас, пока ещё здесь.
  const [sentTo, setSentTo] = useState("");

  // Цель Метрики на ФАКТ показа стены — ровно как horar_limit в
  // HoraryLimitModal. Без неё натальную воронку нечем измерить: события
  // natal_limit до этого не существовало. place добавлен 16.09: без него
  // два входа в одно окно в Метрике не различить.
  // Один показ — одна цель. Раньше в зависимостях эффекта стоял ещё и place,
  // и любая перерисовка родителя, менявшая его при уже открытом окне,
  // отправляла natal_limit второй раз: в свипе вход с кнопки PDF уходил
  // дважды подряд. Считать показы стеной с задвоением нельзя — воронка врёт.
  //
  // Флаг в ref, а не в состоянии: он не должен вызывать перерисовку и обязан
  // пережить любое число промежуточных рендеров. Сбрасывается на закрытии,
  // поэтому следующее открытие снова считается — как и должно.
  //
  // В зависимостях ТОЛЬКО open. place оттуда убран (19.09): пока он там
  // стоял, эффект перезапускался на каждой смене входа при уже открытом
  // окне, и одного флага для защиты было мало — ровно этим задваивался
  // sticky_mobile. Теперь эффект физически не может запуститься иначе,
  // чем на переходе окна из закрытого в открытое, то есть один раз за
  // показ. Сам place читаем из ref: значение нужно свежее, а
  // перезапускать из-за него эффект нельзя.
  const sentRef = useRef(false);
  const placeRef = useRef(place);
  placeRef.current = place;
  useEffect(() => {
    if (!open) {
      sentRef.current = false;
      return;
    }
    if (sentRef.current) return;
    sentRef.current = true;
    ymGoal("natal_limit", { place: placeRef.current });
  }, [open]);

  // Закрытие окна сбрасывает экран успеха: второе открытие должно показать
  // форму, а не прошлое «письмо отправлено». Само состояние формы (адрес,
  // ошибка, подсказка опечатки) сбрасывать не нужно — закрытое окно
  // возвращает null, и форма размонтируется вместе с ним.
  useEffect(() => {
    if (open) return;
    setSentTo("");
  }, [open]);

  // Окно успеха держится недолго: человеку нужно увидеть адрес, на который
  // ушло письмо (опечатку он заметит только здесь), а дальше под окном его
  // уже ждёт раскрытый разбор или готовящийся PDF. Закрыть можно и руками —
  // крестиком или кнопкой.
  useEffect(() => {
    if (!sentTo) return undefined;
    const timer = window.setTimeout(() => { onClose(); }, 6000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentTo]);

  // Аккаунт создан, человек уже вошёл. Порядок действий тот же, что был до
  // выделения общей формы: показать адрес, засеять профиль рождения, тост,
  // и только потом отдать управление странице.
  const handleRegistered = (value) => {
    setSentTo(value);
    // Профиль рождения из гостевой карты — тот же засев, что на странице
    // регистрации: only_if_absent на сервере сам не даст перезаписать
    // чужое. Ничего не ждём и не даём упасть: аккаунт уже создан.
    try {
      const guest = loadNatalFormFromStorage();
      if (guest && isNatalFormComplete(guest.formData)) {
        seedNatalProfile(guest).catch(() => {});
      }
    } catch (_e) {}
    toast.success("Готово! Тебе открыт доступ на 14 дней", { duration: 6000 });
    // Что доделать после входа, знает страница: раскрыть разбор или отдать
    // PDF. Окно закрывает вызывающий код — ему виднее, когда именно.
    if (typeof onRegistered === "function") onRegistered(place, value);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden"
        data-testid="natal-limit-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100 transition-colors z-10"
          data-testid="natal-limit-modal-close"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>

        <div className="pt-8 pb-3 px-6 text-center">
          {/* Заголовок называет ровно то действие, которое человек только что
              попытался сделать, а не спрашивает почту. Поэтому он разный на
              двух входах: за PDF пришли за файлом, за разбором — за текстом. */}
          <h3
            className="text-xl font-bold text-slate-900 mb-2"
            data-testid="natal-limit-modal-heading"
          >
            {/* Число называем ТОЛЬКО когда оно есть: со входа под колесом
                окно может открыться раньше, чем полоса превью успела отдать
                счёт блоков, и заголовок обещал бы «0 блоков». Фраза та же,
                просто без числа. */}
            {fromFullReport
              ? (blocksTotal > 0
                  ? `Полный разбор твоей карты — ${blocksTotal} ${pluralBlocks(blocksTotal)}`
                  : "Полный разбор твоей карты")
              : "Чтобы скачать PDF, зарегистрируйся"}
          </h3>
          {fromFullReport ? (
            /* Вход с кнопки разбора. Одна строка вместо трёх: человек уже
               прочитал три настоящих блока про свою карту в полосе превью,
               пересказывать ему ценность незачем — нужно снять страх перед
               формой («без привязки карты») и добавить то, чего он ещё не
               видел: PDF и письма. */
            <p className="text-sm text-slate-500 leading-relaxed">
              Плюс PDF карты и 14 дней ежедневных прогнозов на почту.
              Бесплатно, без привязки карты.
            </p>
          ) : (
            <>
              {/* Обещание про 14 дней теперь целиком здесь. Отдельный
                  подзаголовок с ним убран: он повторял то же самое строкой выше. */}
              <p className="text-sm text-slate-500 leading-relaxed">
                Если ты внесёшь свои данные в личном кабинете, мы будем присылать
                твой личный прогноз на каждый день тебе на почту — первые 14 дней
                бесплатно.
              </p>
              <p className="mt-3 text-base font-semibold text-slate-900 leading-snug">
                Разбор сохранится в личном кабинете — не потеряется и будет под рукой.
              </p>
              {/* Гость попадает сюда в том числе кликом по «Скачать PDF карты», а
                  модалка про PDF молчала. Строка стоит последней — вплотную над
                  кнопкой, чтобы обещание читалось прямо перед действием. */}
              <p className="mt-3 text-base font-semibold text-slate-900 leading-snug">
                Расшифровка и PDF-разбор откроются сразу после регистрации.
              </p>
            </>
          )}
        </div>

        {sentTo ? (
          /* Успех. Адрес показываем обязательно: он единственный вход в
             аккаунт (пароля нет), и опечатку человек заметит только сейчас,
             пока ещё здесь. Само действие — разбор или PDF — уже пошло под
             окном, поэтому здесь ни кнопок-переходов, ни обещаний. */
          <div className="px-6 pb-6 space-y-3 text-left" data-testid="natal-wall-success">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
              <p className="text-sm font-semibold text-emerald-900">
                Готово. Тебе открыт доступ на 14 дней
              </p>
              <p className="mt-1 text-xs text-emerald-800 leading-snug" data-testid="natal-wall-sent-to">
                Письмо с кнопкой входа отправлено на{" "}
                <span className="font-semibold">{sentTo}</span>. Пароль не нужен —
                входить будешь по этой ссылке.
              </p>
              <p className="mt-2 text-xs text-emerald-800 leading-snug">
                {fromFullReport
                  ? "Разбор уже открыт под этим окном."
                  : "PDF уже готовится — файл скачается сам, это может занять пару минут."}
              </p>
            </div>
            <Button
              type="button"
              onClick={onClose}
              className="w-full h-11 text-white font-medium"
              style={{ background: PRO_GRADIENT, border: "none" }}
              data-testid="natal-wall-success-close-btn"
            >
              Понятно
            </Button>
          </div>
        ) : (
        <OneLineSignupForm
          idPrefix="natal-wall"
          submitTestId="natal-register-btn"
          submitLabel="Открыть мой разбор"
          loadingLabel="Открываю…"
          footNote="Пароль не нужен: вход по ссылке из письма. Бесплатно, без привязки карты."
          collectFirstName={readNatalFormName}
          submitGoal={{ name: "signup_submit", place: "modal_inline" }}
          registerPlace="modal_inline"
          onRegistered={handleRegistered}
        />
        )}
      </div>
    </div>
  );
}
